import React from 'react';
import {
  Badge, Button, EmptyState, ErrorState, SectionCard, SkeletonRow, Table,
} from '@parsina/framework';
import { History, Lock } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { EVENT_PAGE_SIZE } from '../../constants/list';
import { EditorLabel, useColumnLabel } from './shared';

/** A history snapshot (JSON keyed by column), pretty-printed — or verbatim if it is not JSON. */
function pretty(json) {
  if (!json) return '';
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

/**
 * The append-only history of one country's row (country_profile_changes): who,
 * when, which columns, and the note that says why. The whole row before and
 * after is behind a toggle — JSON is the precise answer when something looks
 * wrong, and noise in every other row.
 *
 * `disabled`: the editor contract is not registered on this deployment
 * (EDITOR_DISABLED) — the history lives behind the same platform_admin gate,
 * so it is unavailable for the same reason, said as such rather than as an error.
 */
export default function HistorySection({
  loading, error, disabled, changes, onRetry,
}) {
  const { t, formatDateTime } = useTranslation();
  const columnLabel = useColumnLabel();
  const [shown, setShown] = React.useState(EVENT_PAGE_SIZE);
  const [open, setOpen] = React.useState(null);

  const rows = (changes || []).slice(0, shown);
  const more = (changes || []).length - rows.length;
  const opened = (changes || []).find((c) => c.id === open);

  let body;
  if (loading) {
    body = (
      <div role="status" aria-busy="true" aria-label={t('countries.history.loading')} data-state="loading" data-testid="country-history-loading">
        <table className="w-full"><tbody>
          {Array.from({ length: 3 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={i} cols={5} />
          ))}
        </tbody></table>
      </div>
    );
  } else if (disabled) {
    body = (
      <EmptyState
        icon={<Lock size={40} strokeWidth={1.5} aria-hidden />}
        title={t('countries.history.disabled.title')}
        description={t('countries.history.disabled.desc')}
        data-testid="country-history-disabled"
      />
    );
  } else if (error) {
    body = (
      <ErrorState
        title={t('countries.history.error')}
        description={error}
        onRetry={onRetry}
        retryLabel={t('common.actions.retry')}
        data-testid="country-history-error"
      />
    );
  } else if (!rows.length) {
    body = (
      <EmptyState
        icon={<History size={40} strokeWidth={1.5} aria-hidden />}
        title={t('countries.history.empty.title')}
        description={t('countries.history.empty.desc')}
        data-testid="country-history-empty"
      />
    );
  } else {
    body = (
      <>
        <Table
          dense
          ariaLabel={t('countries.history.aria')}
          rowKey={(c) => c.id}
          rows={rows}
          rowProps={(c) => ({ 'data-testid': `country-history-row-${c.id}` })}
          data-testid="country-history-table"
          columns={[
            { key: 'at', header: t('countries.history.columns.at'), render: (c) => (c.changedAt ? formatDateTime(c.changedAt) : t('common.value.none')) },
            { key: 'by', header: t('countries.history.columns.by'), render: (c) => <EditorLabel by={c.changedBy} /> },
            {
              key: 'fields',
              header: t('countries.history.columns.fields'),
              render: (c) => (
                <span className="flex flex-wrap gap-1">
                  {c.created ? <Badge variant="outline">{t('countries.history.created')}</Badge> : null}
                  {c.changedFields.map((f) => <Badge key={f} variant="outline">{columnLabel(f)}</Badge>)}
                </span>
              ),
            },
            {
              key: 'note',
              header: t('countries.history.columns.note'),
              render: (c) => (
                <span dir="auto" className="block max-w-[16rem] truncate" title={c.note || ''}>
                  {c.note || t('common.value.none')}
                </span>
              ),
            },
            {
              key: 'detail',
              header: t('countries.history.columns.detail'),
              render: (c) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(open === c.id ? null : c.id)}
                  aria-expanded={open === c.id}
                  aria-label={t('countries.history.showRow')}
                  data-testid={`country-history-toggle-${c.id}`}
                >
                  {open === c.id ? t('common.actions.hide') : t('common.actions.show')}
                </Button>
              ),
            },
          ]}
        />
        {/* Under the table rather than in a row: a <pre> in a cell fights the columns. */}
        {opened ? (
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="country-history-detail">
            {[['before', opened.before], ['after', opened.after]].map(([side, json]) => (
              <div key={side} className="min-w-0">
                <div className="mb-1 text-xs text-muted-foreground">{t(`countries.history.${side}`)}</div>
                <pre {...LTR_TEXT} className="max-h-64 overflow-auto rounded-lg bg-muted p-3 text-[11px] leading-5">
                  {json ? pretty(json) : t('common.value.none')}
                </pre>
              </div>
            ))}
          </div>
        ) : null}
        {more > 0 ? (
          <div className="mt-1">
            <Button variant="outline" size="sm" onClick={() => setShown((n) => n + EVENT_PAGE_SIZE)} data-testid="country-history-more">
              {t('countries.history.more')}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <SectionCard
      id="history"
      title={t('countries.history.title')}
      sub={t('countries.history.sub')}
      icon={History}
      data-testid="country-history"
    >
      {body}
    </SectionCard>
  );
}
